/**
 * Global GPU/viewport diagnostic dialog opened by Cmd/Ctrl+Shift+D.
 * Reachable from any page, so support can collect the diagnostic without
 * needing to land on a stack-routed series first. No current-series
 * context here — blockerCode is always null.
 */

import React from "react";
import { useAtom, useAtomValue } from "jotai";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/design-system/components/react";
import { diagnosticDialogOpenAtom } from "../../atoms/measurementAtoms";
import { gpuVerdictAtom } from "../../atoms/gpuVerdictAtom";
import { decideViewport } from "../../utils/pickViewportComponent";
import { DiagnosticBody } from "./DiagnosticBody";

export const DiagnosticDialog: React.FC = () => {
  const [isOpen, setIsOpen] = useAtom(diagnosticDialogOpenAtom);
  const verdict = useAtomValue(gpuVerdictAtom);
  // Hotkey-opened dialog has no current series — reason is null unless the
  // GPU verdict alone says stack-only. decideViewport(null, ...) returns
  // that without forcing the dialog to know the precedence.
  const reasonLine = decideViewport(null, verdict).reason ?? undefined;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-md bg-bg-2 border-border-strong">
        <DialogHeader>
          <DialogTitle className="text-fg">Viewer diagnostic</DialogTitle>
        </DialogHeader>
        <DiagnosticBody verdict={verdict} blockerCode={null} reasonLine={reasonLine} />
        <div className="pt-2 border-t border-border text-center text-xs text-fg-muted">
          Press{" "}
          <kbd className="px-1.5 py-0.5 bg-surface-2 rounded text-fg-muted font-mono">
            Cmd/Ctrl + Shift + D
          </kbd>{" "}
          to open this dialog
        </div>
      </DialogContent>
    </Dialog>
  );
};
